<script setup lang="ts">
import { useForm } from 'vee-validate'
import { z } from 'zod'

import { BtnGhost, BtnOrange } from '~/components/ui/Buttons'
import VTextInput from '~/components/ui/Inputs/VText.vue'
import VText from '~/components/ui/VText.vue'

import { useFieldValidation } from '~/composables/useFieldValidation'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { useTagStore } from '~/stores/tag'

import socialLinkRegexes from '~/helpers/mediaLinks/socialLinkRegexes'

import { isUrl } from '~/utils/url-validation'

import type Tag from '~/entities/tag'
import type { UserInfluencerState } from '~/stores/userInfluencer'
import type { Platforms } from '~/types/mediaLinks'

type Props = {
  modelValue: UserInfluencerState
}
type Emits = {
  'request-done': []
  'update:modelValue': [influencer: UserInfluencerState]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { coreFetch } = useProvideCoreFetch()

const loading = ref(false)

const { validate: validateForm } = useForm()
const {
  value: linkFieldValue,
  errorMessage: linkFieldErrorMessage,
  handleBlur: linkFieldValidateOnBlur,
} = useFieldValidation('influencerLink', z.string().min(1).refine(isUrl), {
  initialValue: '',
  validateOnValueUpdate: false,
})

const kind = ref<null | Tag>(null)

const { GET_TAG_FROM_ID } = useTagStore()

const canSkip = computed<boolean>(() => {
  if (kind.value === null) return false

  return kind.value.name.match(/manager|mentor/gi) !== null
})
const labelDisplay = computed<number | undefined>(() => {
  if (kind.value === null) return

  if (canSkip.value) return 0
  else if (
    kind.value.name.match(
      /springboard|tremplin|booker|label|media|radio|supervisor|superviseur|editor|editeur|social-media-influencer/gi,
    ) !== null
  )
    return 1
  else if (kind.value.name.match(/channel|chaîne/gi) !== null) return 2
  else if (kind.value.name.match(/journalist|journaliste/gi) !== null) return 3
  else if (kind.value.name.match(/playlist/gi) !== null) return 4
  else return 0
})

const linkTypeKey = computed<Platforms | undefined>(() => {
  if (!linkFieldValue.value) return undefined

  let hadMatch = false
  let key

  for (key in props.modelValue.media_links) {
    if (key === 'pk' || key === 'id') continue

    const reg = socialLinkRegexes[key as Platforms]

    if ((hadMatch = reg && linkFieldValue.value.match(reg) !== null)) break
  }
  if (hadMatch) return key as Platforms
})
const leftPictoIcon = computed<string>(() =>
  linkTypeKey.value ? `fa-brands fa-${linkTypeKey.value}` : 'fa-solid fa-link',
)

const sendBundle = computed<UserInfluencerState>(() => {
  const ret = { ...props.modelValue }

  if (linkTypeKey.value)
    ret.media_links[linkTypeKey.value] = linkFieldValue.value
  else ret.link = linkFieldValue.value

  return ret
})

onMounted(async () => {
  await fetchKinds()
  await nextTick()
  importLink()
})

async function finalize(): Promise<void> {
  if (loading.value) return

  const { valid } = await validateForm()
  if (!valid) return

  patchEntity()
}

function skip(): void {
  emit('request-done')
}

async function fetchKinds(): Promise<void> {
  loading.value = true
  if (kind.value !== null) return

  try {
    const {
      identity: { influencer_kind: influencerKindTags },
    } = await coreFetch.$get<UserInfluencerState['tags']>('/influencer/tags/')
    kind.value = GET_TAG_FROM_ID(influencerKindTags[0]) || null
  } finally {
    loading.value = false
  }
}

async function update(): Promise<void> {
  emit('update:modelValue', sendBundle.value)
  await nextTick()
  emit('request-done')
}

async function patchEntity(): Promise<void> {
  loading.value = true

  try {
    await coreFetch.$patch(
      `/influencer/influencer/${props.modelValue.id}/`,
      sendBundle.value,
    )
  } finally {
    loading.value = false
  }
  update()
}

function importLink(): void {
  if (props.modelValue.link && props.modelValue.link.length) {
    linkFieldValue.value = props.modelValue.link
  } else if (props.modelValue.media_links) {
    for (const key in props.modelValue.media_links) {
      const typedKey = key as Platforms

      if (
        props.modelValue.media_links[typedKey] &&
        props.modelValue.media_links[typedKey].length
      ) {
        linkFieldValue.value = props.modelValue.media_links[typedKey]
        break
      }
    }
  }
}
</script>

<template>
  <div class="tw-grid tw-gap-8">
    <div class="tw-grid tw-gap-2">
      <VText cfg="sans/32/bold">
        {{ $t('influencer.create.link00') }} {{ modelValue.entity }}
      </VText>
      <VText cfg="sans/14/normal" color="gray-500">{{
        $t('influencer.create.link01')
      }}</VText>
    </div>
    <VTextInput
      v-model="linkFieldValue"
      data-test-id="curatorSignupActivityLinkInput"
      :label="$t(`influencer.create.link.labelDisplay${labelDisplay}`)"
      :placeholder="$t('influencer.create.link03')"
      :p-validity="!linkFieldErrorMessage"
      :left-picto="leftPictoIcon"
      @blur="linkFieldValidateOnBlur($event, true)"
      @enter="finalize"
    >
      <template #err-message>{{ linkFieldErrorMessage }}</template>
    </VTextInput>
    <div class="tw-grid tw-gap-2 tw-pt-4">
      <BtnOrange
        data-test-id="curatorSignupNextCTA"
        :disabled="canSkip && linkFieldValue.length === 0"
        @click="finalize"
      >
        <div class="btnText">{{ $t('common.next') }}</div>
      </BtnOrange>
      <BtnGhost v-if="canSkip" @click="skip">
        {{ $t('influencer.create.link05') }}
      </BtnGhost>
    </div>
  </div>
</template>
